// ══════════════════════════════════════════════════════════════════════════
//  [WPT1 2026-08-06] TRAINING MODE — per-account training levels
// ══════════════════════════════════════════════════════════════════════════
//  A new reviewer needs judgment reps on real files. Two things must be true at
//  once: they see the lines WITHOUT the machine's answer, and nothing they decide
//  can reach shared truth. This module is the whole law for both, kept
//  dependency-free so the browser (src/classifier.jsx, src/admin.jsx), the
//  serverless functions (api/db.js, api/claude.js) and the deployed self-test
//  (api/selftest.js) all read the SAME rules rather than three lookalikes.
//
//  Levels (stored on the users-v1 record, set by an admin):
//    null — normal. The full pipeline.
//    "T1" — bare classifier. No rules, no AI, no brain. Every line is Human
//           Review, so every call is the trainee's. $0 of AI per file.
//    "T2" — rules only. classifyDet + the post-passes (law tables and the
//           DB1/DB2/DB3 lookups) run; every AI stage, the brain, the anatomy
//           pre-pass and the parts dimension are skipped. Near-$0 per file.
//
//  Two laws, and they are different in kind:
//
//   1. WHAT RUNS (`trainingPlan`) is a training-quality control. The client
//      consults it to skip stages; api/claude.js enforces the AI half so the
//      level cannot be switched off from a browser console.
//   2. NEVER-LEARN (`canSubmitToKb` / `blocksPromotion`) is a DATA-INTEGRITY
//      law. A trainee's practice reps must never become shared truth. It is
//      enforced server-side in api/db.js (submit refused, corpus records
//      stamped, promotion refused, accuracy rollup skipped) — the client half
//      is honesty, not the gate.
//
//  Admin accounts ignore the flag entirely: an admin who sets it on themselves
//  would silently lose the pipeline with no way back (see effectiveTrainingMode).
//
//  KNOWN BOUND: identity is what makes this enforceable, so the level only binds
//  ACCOUNT tokens. A legacy shared-password login (TEAM_PASSWORD) carries no
//  identity and is therefore never in training mode — retiring that password
//  (PROGRESS pending-input P5) is what closes it.
// ══════════════════════════════════════════════════════════════════════════

export const TRAINING_LEVELS = ["T1", "T2"];

// Anything that is not exactly a known level is "not training" — an unreadable
// or hand-edited flag must never half-apply (a partly-skipped pipeline would
// look like a broken classifier, not like training).
export function normalizeTrainingMode(value) {
  const v = String(value ?? "").trim().toUpperCase();
  return TRAINING_LEVELS.includes(v) ? v : null;
}

// The level that actually applies to a caller. Admins are exempt by design.
export function effectiveTrainingMode({ trainingMode, isAdmin } = {}) {
  if (isAdmin) return null;
  return normalizeTrainingMode(trainingMode);
}

export function isTrainingMode(value) {
  return normalizeTrainingMode(value) !== null;
}

const PLANS = {
  // rules  — classifyDet, the DB1/DB2/DB3 lookups and the post-pass chain
  // ai     — every Anthropic call (parts translation, anatomy, classify,
  //          validate, understand, HR pre-analysis, OEM identification)
  // brain  — precedent/principle/association retrieval (Neon + embeddings)
  // parts  — the OEM parts-eligibility dimension (needs both of the above)
  // learn  — may this account's decisions reach the shared KBs / the brain?
  T1: { rules: false, ai: false, brain: false, parts: false, learn: false,
        label: "T1 — bare classifier",
        detail: "No rules, no AI, no brain — every line is yours to call." },
  T2: { rules: true, ai: false, brain: false, parts: false, learn: false,
        label: "T2 — rules only",
        detail: "The rules engine runs; no AI and no brain — you judge everything it can't." },
};

const FULL_PLAN = { rules: true, ai: true, brain: true, parts: true, learn: true, label: "", detail: "" };

// The single answer to "what may this run do?". Callers branch on the booleans;
// they never re-derive them from the level string.
export function trainingPlan(mode) {
  const m = normalizeTrainingMode(mode);
  const p = m ? PLANS[m] : FULL_PLAN;
  return { mode: m, active: m !== null, ...p };
}

// ── The never-learn law ───────────────────────────────────────────────────
// One sentence, three call sites: api/db.js `submit`, the approve paths, and the
// client's own save. A training account teaches nothing, ever.
export function canSubmitToKb(mode) {
  return !isTrainingMode(mode);
}

export const TRAINING_SUBMIT_REFUSAL =
  "Training accounts don't teach the system — your decisions stay in this project. " +
  "Ask the admin to take you out of training mode when you're ready to contribute.";

// Stamp a record/entry/meta object as training work. Server-side this is
// authoritative (the account, not the body, decides); client-side it is a
// courtesy so anything that predates a server stamp still says what it is.
export function stampTraining(obj, mode) {
  const m = normalizeTrainingMode(mode);
  if (!m || !obj || typeof obj !== "object") return obj;
  return { ...obj, training: m };
}

// True for anything carrying a training stamp, whatever shape it arrived in.
// `training: true` is accepted as well as a level string so a stamp written by
// an older client still blocks promotion.
export function isTrainingRecord(x) {
  if (!x || typeof x !== "object") return false;
  return x.training === true || isTrainingMode(x.training);
}

// The approve/promote gate: a stamped entry never becomes shared truth, no
// matter which queue or which admin action it arrived through.
export function blocksPromotion(entry) {
  return isTrainingRecord(entry);
}

// Split a batch at the gate so an approve path can promote the clean entries and
// SAY what it refused, rather than silently dropping rows.
export function partitionTraining(entries) {
  const list = Array.isArray(entries) ? entries : [];
  return {
    promotable: list.filter(e => !blocksPromotion(e)),
    training: list.filter(e => blocksPromotion(e)),
  };
}

// ── Project + stats exclusion ─────────────────────────────────────────────
// A training project carries the stamp and carries NO accuracy summary: the
// platform rollup (api/db.js `accuracy-global`) only counts projects that have
// one, so dropping it is the primary exclusion and the stamp is the check that
// makes the exclusion visible and testable.
export function trainingProjectMeta(meta, mode) {
  const m = normalizeTrainingMode(mode);
  if (!m) return meta;
  const { accuracy, ...rest } = meta || {};
  return { ...rest, training: m };
}

export function countsTowardAccuracy(project) {
  return !!project && !isTrainingRecord(project) && !!project.accuracy;
}
